/**
 * A UI language a user can pick in Settings (TO-197). Persisted as
 * `varchar(2)` in `users.ui_language` — never a Postgres ENUM. The SPA's
 * supported languages; `null` on the profile means "automatic".
 */
export enum UiLanguage {
	ES = 'es',
	GL = 'gl',
	EN = 'en',
	PT = 'pt',
}

/** Where a user's effective display name comes from (TO-197). */
export enum DisplayNameSource {
	/** The user set an override in Settings; IdP logins leave it in place. */
	USER = 'USER',
	/** The latest IdP-delivered value (or the pre-TO-197 name). */
	IDP = 'IDP',
}

/** A user's personal contact overrides for offer landings (TO-197). `null` = no override. */
export interface PersonalContactOverrides {
	phone: string | null
	email: string | null
}

/**
 * The contact card of a user as an offer landing would show it (TO-197): the
 * effective display name, the profile photo (only an `https:` URL), the
 * personal phone else the IdP phone, the personal email else the account email.
 * Absent parts are omitted, never `null`.
 */
export interface OfferContactCard {
	name: string
	picture?: string
	phone?: string
	email?: string
}

/** `GET` / `PATCH /users/me/profile` response (TO-197). */
export interface UserProfileDto {
	userId: string
	/** Account email; read-only here. */
	email: string
	/** Profile photo URL; read-only here. */
	picture: string | null
	/** The effective display name every TODAY surface shows. */
	displayName: string
	displayNameSource: DisplayNameSource
	/** The IdP default display name a reset returns to. */
	idpDisplayName: string
	/** The chosen UI language; `null` = automatic. */
	language: UiLanguage | null
	/** The IdP-synced BCP-47 locale; read-only. */
	idpLocale: string | null
	/** IANA timezone; `null` = the device timezone. */
	timezone: string | null
	showPersonalContactOnOffers: boolean
	personalContact: PersonalContactOverrides
	/** The card a landing would show now if the toggle is on. */
	offerContactCard: OfferContactCard
}
